import '@emotion/react'
import { createTheme } from '@mui/material'
import { getPxAsRem } from 'shared/utils'

/**
 * Add new properties and variants to the theme so they'll be included in the
 * type system and exposed to IDE autocompletion features.
 *
 * @see https://v5.mui.com/material-ui/customization/theming/#typescript
 */
declare module '@mui/material/styles' {
    interface TypographyVariants {
        bodySans1: React.CSSProperties
        detail: React.CSSProperties
    }

    // Allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        bodySans1?: React.CSSProperties
        detail?: React.CSSProperties
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        bodySans1: true
        detail: true
    }
}

/*
overwrites default theme from MUI for specific properties- unspecified values are still the default which can be viewed here:
https://mui.com/material-ui/customization/default-theme/
*/

let theme = createTheme({
    typography: {
        bodySans1: {
            fontSize: '1rem',
            fontFamily: 'Roboto',
        },
    },
    palette: {
        greyscale: {
            white: '#fff',
            grey_100: '#C6C6C6',
            grey_600: '#757575',
            grey_800: '#707070',
            black: '#000',
        },
        primary: {
            main: '#007BC7',
            light: '#DBEDFF',
            dark: '#00456F',
        },
        status: {
            grey: '#9E9E9E',
            red: '#EF5350',
            orange: '#FF7043',
            pink: '#EC407A',
            blue: '#3F8FDA',
            purple: '#7E57C2',
            teal: '#26A69A',
            bluegrey: '#59779A',
            green: '#66BB6A',
        },
        other: {
            primary_50: '#E1F6FF',
            primary_100: '#0288D1', // doesn't have a name in figma but used in task button
            focus_dark: 'rgba(0, 0, 0, 0.12)', // comes from mui menu but needs to be reused,
            outlinedBorder: 'rgba(0, 110, 209, 0.50)', // named color from figma in "add additional graduate degree" button
            secondary_light: 'rgba(0, 0, 0, 0.6)',
            lightBorder: 'rgba(0, 0, 0, 0.1)',
            link: '#007BC7',
            background: '#f1f7ff',
            light_blue: '#1976d2',
            // these colors come from MUI defaults for star rating but need to be reused when showing individual stars
            lightGrey: '#F7F7F7',
            starRating: '#FFB400',
            starRatingDisabled: '#0000003B',
        },
        secondary: {
            main: '#006ED1',
            dark: '#001d33',
        },
        info: {
            main: '#0288D1',
        },
        success: {
            main: '#42BE65',
        },
    },
    fontSize: {
        xsmall: '0.75rem',
    },
    components: {
        MuiFormControl: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0px 2px 12px 6px rgba(231, 241, 255, 0.40)',
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h1',
                    h3: 'h1',
                },
            },
            styleOverrides: {
                h2: {
                    fontFamily: 'Roboto Serif',
                    fontSize: getPxAsRem(34),
                },
                h3: {
                    fontFamily: 'Roboto Serif',
                    fontSize: getPxAsRem(24),
                },
                h4: {
                    fontFamily: 'Roboto Serif',
                    fontSize: getPxAsRem(20),
                    fontWeight: 500,
                    lineHeight: 1.6,
                },
                h5: {
                    fontFamily: 'Roboto Serif',
                    fontSize: getPxAsRem(18),
                    fontWeight: 400,
                },
                h6: {
                    fontFamily: 'Roboto Serif',
                    fontSize: '0.875rem',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
            },
            styleOverrides: {
                outlined: {
                    // outlined and contained buttons have different paddings by default from MUI :/ this makes them the same
                    padding: getPxAsRem(6, 16),
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                },
                input: {
                    '&:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 100px #fff inset',
                    },
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'none',
            },
        },
    },
})

// Redefine the theme to extend it with options that are composed from pre-
// existing or previously defined theme values.
theme = createTheme(theme, {
    typography: {
        detail: {
            color: theme.palette.text.secondary,
            fontFamily: 'Roboto',
            fontSize: getPxAsRem(14),
        },
    },
})

declare module '@mui/material/styles' {
    interface Theme {
        greyscale: {
            white: string
            black: string
            grey_100: string
            grey_600: string
        }
        other: {
            primary_50: string
            primary_100: string
            focus_dark: string
            outlinedBorder: string
            secondary_light: string
            starRating: string
            light_blue: string
            starRatingDisabled: string
            lightBorder: string
            lightGrey: string
        }
        status: {
            grey: string
            red: string
            orange: string
            pink: string
            blue: string
            purple: string
            teal: string
            bluegrey: string
            green: string
        }
        fontSize: {
            xsmall: string
        }
    }

    interface PaletteOptions {
        greyscale?: {
            white?: string
            black?: string
            grey_100?: string
            grey_600?: string
            grey_800?: string
        }
        status?: {
            grey: string
            red: string
            orange: string
            pink: string
            blue: string
            purple: string
            teal: string
            bluegrey: string
            green: string
        }
        other?: {
            primary_50?: string
            primary_100?: string
            focus_dark?: string
            outlinedBorder?: string
            lightBorder: string
            secondary_light: string
            link?: string
            light_blue: string
            background?: string
            starRating: string
            starRatingDisabled: string
            lightGrey?: string
        }
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        fontSize: { xsmall: string }
        status?: {
            danger?: string
            grey: string
            red: string
            orange: string
            pink: string
            blue: string
            purple: string
            teal: string
            bluegrey: string
            green: string
        }
        greyscale?: {
            white?: string
            black?: string
            grey_100?: string
            grey_600?: string
            grey_800?: string
        }
        other?: {
            primary_50?: string
            primary_100?: string
            focus_dark?: string
            outlinedBorder?: string
            lightBorder?: string
            secondary_light: string
            link?: string
            light_blue: string
            background?: string
            lightGrey?: string
        }
    }
}

declare module '@emotion/react' {
    export interface Theme {
        palette: {
            primary: {
                main: string
                dark: string
                light: string
            }
            other: {
                primary_50: string
                primary_100: string
                focus_dark: string
                outlinedBorder: string
                secondary_light: string
                link: string
                light_blue: string
                background: string
                starRating: string
                starRatingDisabled: string
                lightBorder?: string
                lightGrey: string
            }
            status: {
                grey: string
                red: string
                orange: string
                pink: string
                blue: string
                purple: string
                teal: string
                bluegrey: string
                green: string
            }
            secondary: { main: string; outlinedBorder: string; dark: string }
            greyscale: {
                white: string
                black: string
                grey_100: string
                grey_600: string
                grey_800: string
            }
            info: { main: string }
            success: { main: string }
            error: { main: string }
            warning: { main: string }
        }
        fontSize: {
            xsmall: string
        }
    }
}

export default theme
